import React, { lazy } from 'react';
import { CheckCircleIcon, UsersIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { SectionHeading } from '../components/SectionHeading';
import { PageHero } from '../components/PageHero';
import { CTASection } from '../components/CTASection';
interface OverOnsPageProps {
  onNavigate: (page: string) => void;
}
export function OverOnsPage({
  onNavigate
}: OverOnsPageProps) {
  return <div className="w-full" data-id="element-607">
      {/* Hero */}
      <PageHero title="Jouw persoonlijke adviseur" subtitle="Met meer dan 15 jaar ervaring in de financiële dienstverlening. Persoonlijk, snel en zonder gedoe." label="Over Jordy" data-id="element-608" />

      {/* Editorial Layout - Part 1 */}
      <section className="py-20 md:py-28 bg-white" data-id="element-609">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-610">
          <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-611">
            <div className="w-full lg:w-1/2 order-2 lg:order-1" data-id="element-612">
              <FadeIn data-id="element-613">
                <div className="space-y-6 text-lg text-gray-600 leading-relaxed" data-id="element-614">
                  <p data-id="element-615">
                    Hoi, ik ben Jordy de Gier. Met meer dan 15 jaar ervaring in
                    de financiële dienstverlening heb ik JDG Hypotheken |
                    Verzekeringen opgericht vanuit één duidelijke visie:
                    financieel advies moet persoonlijker, sneller en zonder
                    gedoe.
                  </p>
                  <p data-id="element-616">
                    Ik geloof niet in corporate onzin, ingewikkelde keuzemenu's
                    of wisselende contactpersonen. Financiële keuzes zijn vaak
                    de belangrijkste beslissingen in je leven, en die maak je
                    het best met iemand die je kent en vertrouwt.
                  </p>
                  <p data-id="element-617">
                    Waarom ik dit doe? Omdat ik het verschil wil maken op het
                    moment dat het ertoe doet. Als een bank 'nee' zegt tegen een
                    starter, als een ondernemer vastloopt, of als iemand na een
                    scheiding niet weet of ze in het huis kan blijven wonen. Dán
                    wil ik degene zijn die wél een oplossing vindt.
                  </p>
                </div>
              </FadeIn>
            </div>

            <div className="w-full lg:w-1/2 order-1 lg:order-2 relative" data-id="element-618">
              <FadeIn delay={0.2} className="relative z-10" data-id="element-619">
                <img src="https://cdn.magicpatterns.com/uploads/8YqgEQJ5jSY63NDsDuuJzL/5A2A7661-bewerkt-3.jpg" alt="Jordy de Gier" loading="lazy" className="w-full max-w-md mx-auto rounded-2xl shadow-card-lg object-cover aspect-[3/4]" data-id="element-620" />
              </FadeIn>
              <div className="absolute top-10 -right-10 w-full h-full bg-warm rounded-2xl -z-10 hidden md:block" data-id="element-621" />
            </div>
          </div>
        </div>
      </section>

      {/* Ons Team Section */}
      <section className="bg-warm py-20 md:py-28" data-id="element-622">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-623">
          <SectionHeading title="Ons Team" subtitle="Klein genoeg voor persoonlijk contact, groot genoeg om altijd bereikbaar te zijn." data-id="element-624" />

          <div className="max-w-3xl mx-auto text-center" data-id="element-625">
            <FadeIn delay={0.2} className="bg-white p-10 rounded-2xl shadow-card border border-gray-100" data-id="element-626">
              <div className="w-16 h-16 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-627">
                <UsersIcon className="w-8 h-8 text-gold" data-id="element-628" />
              </div>
              <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-629">
                JDG Hypotheken | Verzekeringen bestaat momenteel uit een hecht
                team van 3 professionals. We werken nauw samen, waardoor we
                altijd op de hoogte zijn van jouw dossier.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-630">
                Hoewel we ambities hebben om te groeien, zal dit nooit ten koste
                gaan van het persoonlijke contact. Je spreekt altijd direct met
                iemand die jou en jouw situatie kent.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Erkend Financieel Adviseur Badge Section */}
      <section className="bg-navy py-20 md:py-28 text-white" data-id="element-631">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-632">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-633">
            <FadeIn className="w-full md:w-1/3 flex justify-center" data-id="element-634">
              <div className="bg-white p-6 rounded-2xl shadow-xl" data-id="element-635">
                <img src="https://cdn.magicpatterns.com/uploads/hLrKue75gCT8qjkamHbfdK/JDG_Erkend_adviseur.png" alt="Erkend Financieel Adviseur" className="w-48 h-auto object-contain" data-id="element-636" />
              </div>
            </FadeIn>
            <div className="w-full md:w-2/3" data-id="element-637">
              <FadeIn delay={0.2} data-id="element-638">
                <h2 className="text-3xl font-bold text-gold mb-6" data-id="element-639">
                  Erkend Financieel Adviseur
                </h2>
                <p className="text-lg text-gray-300 leading-relaxed mb-8" data-id="element-640">
                  Wij voldoen aan de hoogste kwaliteitseisen in de branche.
                  Doorlopend bijgeschoold en getoetst. Zo ben jij altijd
                  verzekerd van actueel, onafhankelijk en betrouwbaar advies.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-641">
                  <div className="flex items-center gap-3" data-id="element-642">
                    <CheckCircleIcon className="w-6 h-6 text-gold flex-shrink-0" data-id="element-643" />
                    <span className="font-medium" data-id="element-644">
                      Wft-vergunning Hypothecair Krediet
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-645">
                    <CheckCircleIcon className="w-6 h-6 text-gold flex-shrink-0" data-id="element-646" />
                    <span className="font-medium" data-id="element-647">
                      Wft-vergunning Schadeverzekeringen
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-648">
                    <CheckCircleIcon className="w-6 h-6 text-gold flex-shrink-0" data-id="element-649" />
                    <span className="font-medium" data-id="element-650">
                      Wft-vergunning Levensverzekeringen
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-651">
                    <CheckCircleIcon className="w-6 h-6 text-gold flex-shrink-0" data-id="element-652" />
                    <span className="font-medium" data-id="element-653">
                      Erkend Financieel Adviseur
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-654">
                    <CheckCircleIcon className="w-6 h-6 text-gold flex-shrink-0" data-id="element-655" />
                    <span className="font-medium" data-id="element-656">Aangesloten bij Kifid</span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-657">
                    <CheckCircleIcon className="w-6 h-6 text-gold flex-shrink-0" data-id="element-658" />
                    <span className="font-medium" data-id="element-659">
                      Doorlopende PE-bijscholing
                    </span>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial Layout - Part 2 */}
      <section className="py-20 md:py-28 bg-white" data-id="element-660">
        <div className="max-w-7xl mx-auto px-4 md:px-8" data-id="element-661">
          <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-662">
            <div className="w-full lg:w-1/2 relative" data-id="element-663">
              <FadeIn className="relative z-10" data-id="element-664">
                <img src="https://cdn.magicpatterns.com/uploads/xuQDP4soQA1T2E7yMeP3Fj/5A2A7557-bewerkt-3.jpg" alt="Jordy de Gier in kantoor" loading="lazy" className="w-full max-w-md mx-auto rounded-2xl shadow-card-lg object-cover aspect-square" data-id="element-665" />
              </FadeIn>
              <div className="absolute -bottom-10 -left-10 w-full h-full bg-navy/5 rounded-2xl -z-10 hidden md:block" data-id="element-666" />
            </div>

            <div className="w-full lg:w-1/2" data-id="element-667">
              <FadeIn delay={0.2} data-id="element-668">
                <h2 className="text-3xl font-bold text-navy mb-6" data-id="element-669">
                  Lokaal geworteld, landelijk actief
                </h2>
                <div className="space-y-6 text-lg text-gray-600 leading-relaxed" data-id="element-670">
                  <p data-id="element-671">
                    Hoewel ik klanten door het hele land help, ligt ons hart in
                    de regio Badhoevedorp, Amsterdam en Hoofddorp, de
                    Haarlemmermeer en de Schiphol-regio. Ik ken de lokale markt
                    als geen ander.
                  </p>
                  <p data-id="element-672">
                    Of je nu starter bent, ondernemer, of in een scheiding ligt:
                    ik neem de tijd voor je. Geen snelle verkooppraatjes, maar
                    een duurzame relatie. Veel van mijn klanten bellen me jaren
                    later nog steeds voor advies over pensioen,
                    belastingaangifte of andere financiële vraagstukken. Dat is
                    voor ons het ultieme bewijs van vertrouwen.
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Office Location Section */}
      <section className="bg-warm py-20 md:py-28" data-id="element-673">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center" data-id="element-674">
          <FadeIn data-id="element-675">
            <h2 className="text-3xl font-bold text-navy mb-12" data-id="element-676">
              Kom gerust langs
            </h2>
            <div className="relative max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-card-lg" data-id="element-677">
              <img src="https://cdn.magicpatterns.com/uploads/kirqiWzVxRbHNevkp7sjEZ/JDG_Nieuwsbrief_heading_goed_zonder_(1).jpg" alt="JDG Kantoor Badhoevedorp" loading="lazy" className="w-full h-auto object-cover" data-id="element-678" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent flex items-end justify-center pb-10" data-id="element-679">
                <div className="text-white" data-id="element-680">
                  <p className="text-2xl font-bold mb-2" data-id="element-681">Sloterweg 123</p>
                  <p className="text-lg opacity-90" data-id="element-682">Badhoevedorp</p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection title="Benieuwd wat we voor jou kunnen betekenen?" subtitle="Plan een vrijblijvende kennismaking. We nemen de tijd om jouw situatie te begrijpen." buttonText="Neem contact op" onNavigate={onNavigate} page="contact" variant="light" data-id="element-683" />
    </div>;
}